import { Form, Icon, List, Message } from "semantic-ui-react";
import RateCard from "../../components/RateCard";
import { useEffect, useState, useCallback, useRef } from "react";
import { Hotels, Rates, PriceLists } from "@api/services/next";
import { useLoader } from "@hooks/useLoader";
import { useTextResources } from "@/language";
import moment from "moment";

// Helper to flatten RateCustomNames for ASP.NET Core model binding
const flattenRateCustomNames = (spec) => {
  const flattenedSpec = {
    SourceCopyRateId: spec.SourceCopyRateId,
    TargetHotelIds: spec.TargetHotelIds,
  };

  if (spec.RateCustomNames?.length > 0) {
    spec.RateCustomNames.forEach((item, index) => {
      if (item.HotelId !== undefined) {
        flattenedSpec[`RateCustomNames[${index}].HotelId`] = item.HotelId;
      }
      if (item.RateName !== undefined) {
        flattenedSpec[`RateCustomNames[${index}].RateName`] = item.RateName;
      }
    });
  }

  return flattenedSpec;
};

const ConfirmDetails = ({ state, setState }) => {
  const loader = useLoader();
  const [hotels, setHotels] = useState([]);
  const [rateDates, setRateDates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const text = useTextResources();
  const initialLoadDoneRef = useRef(false);

  // Store setState and loader in refs to avoid dependency issues
  const setStateRef = useRef(setState);
  const loaderRef = useRef(loader);
  
  // Keep refs up to date
  useEffect(() => {
    setStateRef.current = setState;
    loaderRef.current = loader;
  });

  // Combined effect for initial data loading
  useEffect(() => {
    // Prevent double execution in strict mode or re-renders
    if (initialLoadDoneRef.current) return;
    
    const loadInitialData = async () => {
      if (!state.SourceCopyRateId || !state.TargetHotelIds?.length) return;

      initialLoadDoneRef.current = true;
      loaderRef.current(true);
      setIsLoading(true);
      
      try {
        // Fetch all data in parallel
        const [ratesData, hotelsData, validationResults] = await Promise.all([
          Rates.GetRates({ Id: state.SourceCopyRateId }),
          Hotels.GetHotels({ Ids: state.TargetHotelIds }),
          PriceLists.ValidateRateCopy(
            flattenRateCustomNames({
              SourceCopyRateId: state.SourceCopyRateId,
              TargetHotelIds: state.TargetHotelIds,
              RateCustomNames: state.RateCustomNames,
            })
          ),
        ]);

        // Process rate dates
        const dates = ratesData.map(({ Id, StartDate, EndDate, HotelId }) => ({
          Id,
          StartDate,
          EndDate,
          HotelId,
        }));
        setRateDates(dates);

        // Process hotels
        setHotels(hotelsData);

        // Update state with hotels and validation results
        setStateRef.current((prev) => {
          const mergedNames = prev.RateCustomNames.map((rcn) => {
            const validationResult = validationResults.RateCustomNames.find(
              (r) => r.HotelId === rcn.HotelId
            );
            return {
              ...rcn,
              IsNameValid: validationResult?.IsNameValid ?? rcn.IsNameValid,
            };
          });

          return {
            ...prev,
            Hotels: hotelsData,
            AllValid: validationResults.AllValid,
            RateCustomNames: mergedNames,
          };
        });
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        loaderRef.current(false);
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [state.SourceCopyRateId, state.TargetHotelIds, state.RateCustomNames]);

  const validateRateNames = useCallback(
    async (rateCustomNames) => {
      if (!state.SourceCopyRateId || !state.TargetHotelIds?.length) return;

      loader(true);
      setIsLoading(true);

      try {
        const results = await PriceLists.ValidateRateCopy(
          flattenRateCustomNames({
            SourceCopyRateId: state.SourceCopyRateId,
            TargetHotelIds: state.TargetHotelIds,
            RateCustomNames: rateCustomNames,
          })
        );

        setState((prev) => {
          const mergedNames = prev.RateCustomNames.map((rcn) => {
            const validationResult = results.RateCustomNames.find(
              (r) => r.HotelId === rcn.HotelId
            );
            return {
              ...rcn,
              IsNameValid: validationResult?.IsNameValid ?? rcn.IsNameValid,
            };
          });

          return {
            ...prev,
            AllValid: results.AllValid,
            RateCustomNames: mergedNames,
          };
        });
      } catch (error) {
        console.error("Error validating rate names:", error);
      } finally {
        loader(false);
        setIsLoading(false);
      }
    },
    [state.SourceCopyRateId, state.TargetHotelIds, setState, loader]
  );

  const getHotelName = (hotelId) => {
    return hotels.find((h) => h.Id === hotelId)?.Name || "Unknown Hotel";
  };

  const handleNameChange = async (hotelId, newName) => {
    const updatedNames = state.RateCustomNames.map((rcn) =>
      rcn.HotelId === hotelId ? { ...rcn, RateName: newName } : rcn
    );

    setState((prev) => ({ ...prev, RateCustomNames: updatedNames }));
    await validateRateNames(updatedNames);
  };

  const handleRateRemove = (hotelId) => {
    setState((prev) => ({
      ...prev,
      RateCustomNames: prev.RateCustomNames.filter(
        (rcn) => rcn.HotelId !== hotelId
      ),
      TargetHotelIds: prev.TargetHotelIds.filter((id) => id !== hotelId),
    }));
  };

  const isReady =
    state.SourceCopyRate &&
    state.RateCustomNames?.every(({ IsNameValid }) => IsNameValid !== null);

  return (
    <Form>
      {isReady && !isLoading && (
        <List relaxed>
          {rateDates.some((dates) =>
            moment(dates.StartDate).isBefore(moment(), "day")
          ) && (
            <List.Item>
              <Message info>
                <Icon name="info circle" />
                {text("RatesWithPastDates")}
              </Message>
            </List.Item>
          )}
          {state.RateCustomNames.map(({ HotelId, RateName, IsNameValid }) => (
            <List.Item key={HotelId}>
              <RateCard
                name={RateName}
                hotelName={getHotelName(HotelId)}
                isValid={IsNameValid}
                dates={rateDates.find((r) => r.HotelId === HotelId)}
                onNameChange={(newName) => handleNameChange(HotelId, newName)}
                onRateRemove={() => handleRateRemove(HotelId)}
              />
            </List.Item>
          ))}
        </List>
      )}
    </Form>
  );
};

export default ConfirmDetails;
